import * as d3 from 'd3'
import * as dc from 'dc'
import { VOLUME_BUCKETS, volumeIndexFromNotional } from '@/utils/flowSightBuckets'

// "Clients by Notional Range" row chart (Overview tab). Counts DISTINCT
// counterparties per notional bucket (not a running sum/count) via a
// crossfilter reduce that accumulates a Map of counterparty -> row count,
// then unwraps the Map to its .size for dc's row chart to consume (row/bar
// charts expect numeric values, not Maps).
//
// A plain Set (add/delete by counterparty name) is NOT safe here: when
// another chart's filter (e.g. drilling down to status=DEAL) excludes some
// rows, crossfilter calls the reduce's "remove" once per excluded row. If a
// counterparty has several rows in the same bucket, the first excluded row
// deletes that counterparty from the Set outright, even though other rows
// from the same counterparty are still active in the bucket - the whole
// bucket can incorrectly collapse to 0. A reference-counted Map (increment
// on add, only delete once the count hits 0) keeps the distinct count
// correct under any combination of filters.
export function createClientsVolumeChart ({ el, ndx, chartGroup, width, color, t }) {
  // Keyed by bucket INDEX, not label - the padding and the row ordering below
  // are then plain integer maths, same shape as createClientsMaturityChart.
  const dim = ndx.dimension((d) => volumeIndexFromNotional(d.notional))
  const group = dim.group().reduce(
    (p, v) => {
      const key = v.counterparty
      p.set(key, (p.get(key) || 0) + 1)
      return p
    },
    (p, v) => {
      const key = v.counterparty
      const count = p.get(key)
      if (count <= 1) p.delete(key)
      else p.set(key, count - 1)
      return p
    },
    () => new Map()
  )
  // Unwraps each Map to its .size (row charts need numbers) and pads the
  // buckets crossfilter has no rows for, so the ladder keeps all five rungs
  // instead of dropping one - and resizing the others - whenever a filter
  // empties a band.
  const groupCount = {
    all: () => {
      const map = new Map(group.all().map((d) => [d.key, d.value.size]))
      return VOLUME_BUCKETS.map((_, i) => ({ key: i, value: map.get(i) || 0 }))
    },
  }

  const chart = new dc.RowChart(el, chartGroup)
  chart
    .width(width)
    // 225 here and 225 on "Clients by Maturity Range" below: both charts carry
    // 5 buckets now, so they get equal height and equal bar thickness (a
    // RowChart derives thickness from height / row count). The pair must keep
    // summing to 450 - that is what the left column's 250 (status) + 200 (side)
    // adds up to, and it is the only thing holding the two columns aligned.
    .height(225)
    .x(d3.scaleLinear().domain([0, d3.max(groupCount.all(), (d) => d.value)]))
    .margins({ top: 0, right: 0, bottom: 50, left: 10 })
    .elasticX(true)
    // Rows read bottom-up: 0-100k at the bottom, >15M at the top, regardless of
    // how many clients land in each. dc renders the FIRST ordered datum at the
    // top, hence the negated key.
    //
    // This override is not optional: CapMixin (which RowChart mixes in) ships
    // `ordering(kv => -kv.value)` to emulate group.top(N), so without it the
    // rows sort by client count and the buckets come out shuffled.
    .ordering((kv) => -kv.key)
    .dimension(dim)
    .group(groupCount)
    .controlsUseVisibility(true)
    .colors(color)
    .renderLabel(true)
    .label((d) => {
      const total = d3.sum(groupCount.all(), (kv) => kv.value)
      const percent = total > 0 ? (d.value / total) * 100 : 0
      return `${VOLUME_BUCKETS[d.key].label} (${percent.toFixed(0)}%)`
    })
    .title((d) => `${VOLUME_BUCKETS[d.key].label}: ${d.value}`)
    // dc's RowChart has no built-in x-gridline option (unlike BarChart's
    // renderVerticalGridLines), so this hand-draws one via the chart's own
    // x-scale ticks on every transition.
    .on('pretransition', (c) => {
      const body = c.select('g.chart-body')
      const x = c.x()
      const h = c.effectiveHeight()
      const ticks = x.ticks(20)
      const lines = body.selectAll('line.grid-y').data(ticks)
      lines
        .enter()
        .append('line')
        .attr('class', 'grid-y')
        .merge(lines)
        .attr('x1', (d) => x(d))
        .attr('x2', (d) => x(d))
        .attr('y1', 0)
        .attr('y2', h)
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1)
        .attr('opacity', 0.2)
      lines.exit().remove()
    })

  // dc's RowChart also has no built-in x-axis-label API (unlike BarChart's
  // .xAxisLabel()) - hand-appended here. Selected-then-appended rather than
  // appended outright, so redraws reposition the one label instead of stacking
  // a new <text> on top of it every cycle.
  chart.on('renderlet', (c) => {
    const svg = c.select('svg')
    let label = svg.select('text.x-axis-label')
    if (label.empty()) label = svg.append('text').attr('class', 'x-axis-label')
    label
      .attr('x', 75)
      .attr('y', c.height() - 10)
      .attr('text-anchor', 'middle')
      .text(t('flowSightOverview.clientCountAxis'))
  })

  chart.render()
  return chart
}
